// s15 — таб «Маркеры»: все verified биомаркеры пользователя с поиском + фильтрами + grouping.
// Source: docs/specs/sprint-5/stage-4-frontend.md §5.2 + prototypes/index.html #s15.

import { useMemo, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { CalendarDays, ChevronDown, ChevronUp, Search } from 'lucide-react';
import { useBiomarkersList } from '@/api/hooks/biomarkers';
import { useDashboard } from '@/api/hooks/dashboard';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { BottomNav } from '@/components/layout/BottomNav';
import { SafeBiomarkerCard } from '@/components/dashboard/BiomarkerCard';
import { hasHistory } from '@/components/dashboard/HistoryHint';
import { OrganGroupedView } from '@/components/dashboard/OrganGroupedView';
import { ExtrasEntryCard } from '@/components/dashboard/ExtrasEntryCard';
import {
  categoryHeadlineTier,
  categoryNeedsAttention,
  sortByTier,
} from '@/lib/biomarkerStatus';
import { categorySortOrder } from '@/lib/category-labels';
import { catalogMarkersCount } from '@/lib/homeAttention';
import { StatusGlyph } from '@/components/dashboard/StatusGlyph';
import { CatalogSkeleton } from '@/components/Skeletons';
import { cn } from '@/lib/utils';
import {
  readListScreenState,
  saveListScreenState,
  setMarkerOrigin,
  type MarkersSegment,
} from '@/lib/markerNavState';
import type { BiomarkerListFilter, BiomarkerListItem } from '@/api/types';

type Group = [code: string, label: string, items: BiomarkerListItem[]];

/**
 * Pure helper для T-S5-12 vitest. Группирует по category_code, сортирует по
 * sort_order (backend) с fallback categorySortOrder, фильтрует по search-substring
 * по canonical_name_ru (case-insensitive). Label берётся из items[0].category_label_ru
 * (RF-S5-S4-8: backend authoritative).
 */
export function groupByCategory(items: BiomarkerListItem[], search: string): Group[] {
  const q = search.trim().toLowerCase();
  const filtered = q
    ? items.filter((i) => i.canonical_name_ru.toLowerCase().includes(q))
    : items;
  const map = new Map<string, BiomarkerListItem[]>();
  for (const item of filtered) {
    const arr = map.get(item.category_code) ?? [];
    arr.push(item);
    map.set(item.category_code, arr);
  }
  return Array.from(map.entries())
    .map<Group>(([code, group]) => [code, group[0]?.category_label_ru ?? code, group])
    .sort((a, b) => {
      const orderA = a[2][0]?.sort_order ?? categorySortOrder(a[0]);
      const orderB = b[2][0]?.sort_order ?? categorySortOrder(b[0]);
      return orderA - orderB;
    });
}

export function MarkersTab() {
  // HEAL-9: фильтр/поиск/аккордеоны инициализируем из кэша — переживают remount при
  // возврате из карточки маркера.
  const [filter, setFilter] = useState<BiomarkerListFilter>(
    () => readListScreenState('markers')?.filter ?? 'all',
  );
  const [search, setSearch] = useState(() => readListScreenState('markers')?.search ?? '');
  // open-состояние аккордеонов поднято сюда (override авто-expand): code → раскрыта ли,
  // отсутствие кода = дефолт (см. grouped.map). Нужно для точного scroll-restore (req 5).
  const [openByCode, setOpenByCode] = useState<Record<string, boolean>>(
    () => readListScreenState('markers')?.openByCode ?? {},
  );
  // Sprint 6-organs D1/Q5: сегмент «По категориям ⇄ По органам». HEAL-24: дефолт —
  // «По категориям» (группировка привычнее юзеру), он же слева в переключателе.
  const [segment, setSegment] = useState<MarkersSegment>(
    () => readListScreenState('markers')?.segment ?? 'categories',
  );
  const [, setLocation] = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  const { data: list, isPending, isError } = useBiomarkersList(filter);
  const {
    data: dashboard,
    isPending: dashboardPending,
    isError: dashboardError,
  } = useDashboard();

  const grouped = useMemo(() => groupByCategory(list?.items ?? [], search), [list, search]);

  // Снапшот состояния таба перед уходом в карточку (или extras) — ScrollToTop потом
  // восстановит scroll, MarkersTab при remount поднимет фильтр/поиск/аккордеоны из кэша.
  const navigateAway = (target: string) => {
    saveListScreenState('markers', {
      scrollTop: mainRef.current?.scrollTop ?? 0,
      filter,
      search,
      openByCode,
      segment,
    });
    setLocation(target);
  };

  // Origin ставим ТОЛЬКО при переходе в карточку маркера (для history.back() «Назад»),
  // НЕ для extras — иначе stale-origin увёл бы фолбэк не туда.
  const openMarker = (slug: string) => {
    setMarkerOrigin('/markers');
    navigateAway(`/markers/${slug}`);
  };

  // Тайл органа → деталь органа. Origin НЕ ставим (это не карточка маркера);
  // навигация сохраняет состояние таба (scroll/сегмент) для восстановления при возврате.
  const openOrgan = (organCode: string) => {
    navigateAway(`/markers/organ/${organCode}`);
  };

  // Счётчики чипов берём из dashboard.summary — он отдаёт полные числа независимо от
  // активного фильтра. Если брать `list.items`, то после клика «Вне нормы» цифра
  // «В норме · 0» становится ложной (backend уже отфильтровал rows).
  const inRangeCount = dashboard?.summary?.in_range ?? 0;
  // HEAL-36: 🟠 «в норме, но не оптимально» — свой отбор и свой счётчик.
  // Бакеты не пересекаются, поэтому «Все» — честная сумма четырёх.
  const attentionCount = dashboard?.summary?.attention ?? 0;
  const outOfRangeCount = dashboard?.summary?.out_of_range ?? 0;
  // R-2: «Все» = ровно те строки, что вернёт каталог. `no_range` сюда не входит
  // — с этой версии он живёт только в «Дополнительных». Одна функция на две
  // витрины: то же число стоит на кнопке «Все показатели · N» на Главной.
  const totalCount = dashboard?.summary ? catalogMarkersCount(dashboard.summary) : 0;
  const extrasCount = dashboard?.extras_count ?? 0;

  // Sprint 6-organs kill-switch: undefined = dashboard ещё грузится (дефолт ON),
  // false = фича выключена → сегмент прячем, «Маркеры» = только «По категориям».
  const organsNavEnabled = dashboard?.organs_nav_enabled;
  const effectiveSegment: MarkersSegment =
    organsNavEnabled === false ? 'categories' : segment;

  // HEAL-18: одна и та же карточка для обоих сегментов — счётчик берётся из
  // dashboard.extras_count (не зависит от активного сегмента и от фильтра списка),
  // тап ведёт на тот же /markers/extra. Нет показателей вне справочника → null.
  // Без внешних отступов — горизонтальный padding даёт хост (у сегментов он свой).
  const extrasEntry =
    extrasCount > 0 ? (
      <ExtrasEntryCard count={extrasCount} onClick={() => navigateAway('/markers/extra')} />
    ) : null;

  return (
    <MobileFrame>
      <ScreenHeader title="Показатели" />

      <div className="space-y-3 px-4 pb-3 pt-1">
        {dashboard && (
          <p className="flex items-center gap-2 text-hl-small text-muted-foreground">
            <CalendarDays className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
            <span>
              {dashboard.analyses_count}{' '}
              {pluralize(dashboard.analyses_count, ['анализ', 'анализа', 'анализов'])}
              {dashboard.latest_measured_at && (
                <> · последний {formatRu(dashboard.latest_measured_at)}</>
              )}
            </span>
          </p>
        )}

        {organsNavEnabled !== false && (
          <div className="grid grid-cols-2 gap-1 rounded-control bg-muted p-1">
            <SegmentButton
              active={effectiveSegment === 'categories'}
              onClick={() => setSegment('categories')}
              label="По категориям"
            />
            <SegmentButton
              active={effectiveSegment === 'organs'}
              onClick={() => setSegment('organs')}
              label="По органам"
            />
          </div>
        )}

        {effectiveSegment === 'categories' && (
          <>
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Найти показатель"
                className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm"
              />
            </label>

            <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1">
              <FilterChip
                active={filter === 'all'}
                onClick={() => setFilter('all')}
                label="Все"
                count={totalCount}
              />
              <FilterChip
                active={filter === 'out_of_range'}
                onClick={() => setFilter('out_of_range')}
                label="Вне нормы"
                count={outOfRangeCount}
                dotClass="bg-bad"
              />
              {/* HEAL-36: порядок по тревожности — «Вне нормы», потом 🟠, потом
                  «В норме». Чип показываем всегда, даже при нуле: иначе набор
                  фильтров прыгает от бланка к бланку. */}
              <FilterChip
                active={filter === 'attention'}
                onClick={() => setFilter('attention')}
                label="Требуют внимания"
                count={attentionCount}
                dotClass="bg-attention"
              />
              <FilterChip
                active={filter === 'in_range'}
                onClick={() => setFilter('in_range')}
                label="В норме"
                count={inRangeCount}
                dotClass="bg-primary"
              />
            </div>
          </>
        )}
      </div>

      <main ref={mainRef} className="flex flex-1 flex-col gap-3 overflow-y-auto pb-4">
        {/* Органный список зависит от dashboard (флаг + analyses_count). Спиннер —
            только пока dashboard грузится; на error не виснем вечно, а показываем
            ошибку; список монтируем лишь когда флаг точно ON (не дёргаем 404 при OFF). */}
        {effectiveSegment === 'organs' && dashboardPending && <CatalogSkeleton />}
        {effectiveSegment === 'organs' && dashboardError && (
          <div className="p-6 text-center text-sm text-muted-foreground">
            Не удалось загрузить данные. Зайдите позже.
          </div>
        )}
        {effectiveSegment === 'organs' && dashboard?.organs_nav_enabled && (
          <OrganGroupedView
            onOpenOrgan={openOrgan}
            analysesCount={dashboard.analyses_count}
            // HEAL-18: точка входа в «Дополнительные показатели» и в разрезе «По органам».
            // Отдаём слотом (а не рендерим здесь), чтобы карточка не висела под спиннером
            // органов и появлялась в т.ч. в ветке «нет показателей, связанных с органами» —
            // это и есть главный кейс тикета: у юзера ВСЁ распознанное вне справочника.
            footer={extrasEntry}
          />
        )}

        {effectiveSegment === 'categories' && isPending && <CatalogSkeleton />}
        {effectiveSegment === 'categories' && isError && !isPending && (
          <div className="p-6 text-center text-sm text-muted-foreground">
            Не удалось загрузить список показателей.
          </div>
        )}
        {effectiveSegment === 'categories' && !isPending && !isError && grouped.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground">
            {search ? 'Ничего не найдено.' : 'Пока нет показателей.'}
          </div>
        )}
        {effectiveSegment === 'categories' && !isPending && !isError &&
          grouped.map(([code, label, items]) => {
            // Дефолт открытости — авто-expand при наличии out_of_range. Кэш (openByCode)
            // переопределяет его после ручного тапа, переживая remount.
            const defaultOpen = categoryNeedsAttention(items);
            const open = openByCode[code] ?? defaultOpen;
            return (
              <section key={code} className="px-4">
                <CategoryAccordion
                  code={code}
                  label={label}
                  items={items}
                  open={open}
                  onToggle={() => setOpenByCode((prev) => ({ ...prev, [code]: !open }))}
                  onItemClick={openMarker}
                />
              </section>
            );
          })}

        {/* HEAL-18: гейт только по `isPending` (под спиннером карточке не место), но НЕ по
            `isError` — extras_count приходит из /dashboard, независимо от GET /biomarkers.
            Иначе при падении списка маркеров юзер видел бы «Дополнительные · N» в одном
            сегменте и ничего в другом, что ломает AC «одинаковые точки входа». Симметрично
            ветке ошибки в OrganGroupedView. */}
        {effectiveSegment === 'categories' && !isPending && extrasEntry && (
          <section className="px-4 pt-1">{extrasEntry}</section>
        )}
      </main>

      <BottomNav active="markers" />
    </MobileFrame>
  );
}

function SegmentButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'rounded-md py-1.5 text-hl-secondary font-medium transition',
        active ? 'bg-surface text-foreground shadow-sm' : 'text-muted-foreground',
      )}
    >
      {label}
    </button>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  count,
  dotClass,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  dotClass?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex flex-shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-hl-small font-medium transition',
        active
          ? 'bg-gray-900 text-white'
          : 'border border-border bg-surface text-gray-700 hover:bg-muted',
      )}
    >
      {dotClass && <span className={cn('h-2 w-2 rounded-full', dotClass)} aria-hidden="true" />}
      <span>
        {label} · {count}
      </span>
    </button>
  );
}

/**
 * Аккордеон-категория для MarkersTab. Auto-expand если есть out_of_range.
 * Mirror pattern из HomeConfirmed CategoryAccordion, но работает с BiomarkerListItem
 * (без delta — на browse-экране дельты не нужны).
 */
function CategoryAccordion({
  code,
  label,
  items,
  open,
  onToggle,
  onItemClick,
}: {
  code: string;
  label: string;
  items: BiomarkerListItem[];
  open: boolean;
  onToggle: () => void;
  onItemClick: (slug: string) => void;
}) {
  // «Отклонения» = вне нормы (🔴) — красный чип. 🟠 сигналится цветом точки +
  // карточек, без текст-чипа (D1 — слово тира не показываем).
  const outCount = items.filter((i) => i.clinical_status === 'out_of_range').length;
  const headlineTier = categoryHeadlineTier(items);

  return (
    <div className="overflow-hidden rounded-surface border border-border bg-surface">
      <button
        type="button"
        onClick={onToggle}
        aria-controls={`cat-${code}`}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-3 py-3 text-left transition hover:bg-muted"
      >
        {/* D-11: был кружок — одна форма на все четыре статуса. */}
        <StatusGlyph tier={headlineTier} />
        <div className="min-w-0 flex-1">
          <span className="text-sm font-semibold text-foreground">{label}</span>
          <span className="ml-2 text-xs text-muted-foreground">{items.length}</span>
          {outCount > 0 && (
            <span className="ml-2 rounded-full bg-bad/15 px-2 py-0.5 text-hl-small font-semibold text-bad">
              {outCount}{' '}
              {pluralize(outCount, ['отклонение', 'отклонения', 'отклонений'])}
            </span>
          )}
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
        )}
      </button>
      {open && (
        <div id={`cat-${code}`} className="space-y-2 border-t border-border p-3">
          {sortByTier(items).map((item) => (
            <SafeBiomarkerCard
              key={item.biomarker_code}
              name={item.canonical_name_ru}
              normalRangeLabel={item.normal_range_label}
              value={item.value_normalized}
              unit={item.unit_normalized}
              valueText={item.value_qualitative}
              clinicalStatus={item.clinical_status}
              optimumStatus={item.optimum_status}
              delta={item.delta}
              hasHistory={hasHistory(item.points_count)}
              onClick={() => onItemClick(item.biomarker_code)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function pluralize(n: number, forms: [string, string, string]): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
}

function formatRu(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00Z');
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
